import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { RoiId, StructureSetId } from '~/domain/changes/ids';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { DomainError } from '~/domain/errors/DomainError';
import { Roi } from '~/domain/patient/roi';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { useStructureSetService } from './useStructureSetService';

export function useStructureSetCommands() {
    const service = useStructureSetService();
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);

    const wrap = useCallback(
        <T>(op: () => T): T | undefined => {
            try {
                const result = op();
                // A successful domain mutation leaves unsaved work; arm the
                // close/switch-patient guard so it is never lost silently.
                // (The legacy event handlers flip this atom; the command
                // path must too — otherwise removeRoi/rename discard work
                // with no warning.)
                setHasUnsavedChanges(true);
                return result;
            } catch (e) {
                if (e instanceof DomainError) {
                    enqueueSnackbar(e.message, { variant: 'warning' });
                    return undefined;
                }
                throw e;
            }
        },
        [setHasUnsavedChanges]
    );

    const rename = useCallback(
        (id: StructureSetId, newName: string): void => {
            wrap(() => service.rename({ id }, newName));
        },
        [service, wrap]
    );

    const approveAll = useCallback(
        (id: StructureSetId, reviewer: string): void => {
            wrap(() => service.approveAll({ id }, reviewer));
        },
        [service, wrap]
    );

    const createRoi = useCallback(
        (
            id: StructureSetId,
            type: RoiInterpretedType,
            name: string
        ): Roi | undefined => wrap(() => service.createRoi({ id }, type, name)),
        [service, wrap]
    );

    const removeRoi = useCallback(
        (id: RoiId): void => {
            wrap(() => service.removeRoi({ id }));
        },
        [service, wrap]
    );

    const removeRois = useCallback(
        (id: StructureSetId, roiNumbers: readonly number[]): void => {
            wrap(() => service.removeRois({ id }, roiNumbers));
        },
        [service, wrap]
    );

    return { rename, approveAll, createRoi, removeRoi, removeRois };
}
